import React, { useCallback, useMemo } from 'react'

import type { IFormKitControlProps } from 'components/formKit/types'
import Breadcrumbs from 'components/uiKit/Breadcrumbs'
import type { BreadcrumbItem } from 'components/uiKit/Breadcrumbs/Breadcrumbs'
import { isMixed } from 'components/uiKit/KitUtils'
import { ModalTable } from 'components/uiKit/Table'
import { useHighlightedItems, useTableState } from 'components/uiKit/Table/table.hooks'
import type { ColumnsType, Highlighted } from 'components/uiKit/Table/types'
import { getTableErrorInfo } from 'components/uiKit/Table/utils'
import type {
  ProjectGroupListSchemaFragment,
  ProjectListSchemaFragment,
} from 'gql/__generated__/graphql'
import { useProjectGroupAll } from 'gql/projectGroups/apollo'
import {
  checkIsFolder,
  checkIsProject,
} from 'routes/routes/App/routes/Company/routes/Catalog/utils'
import { t } from 'services/Translation'

import * as s from './FieldProjectFolder.module.scss'

interface IFieldProjectFolderValue {
  categoryId: string
  color?: string | null
  item?: ProjectGroupListSchemaFragment | ProjectListSchemaFragment
}

export interface IFieldProjectFolderProps extends IFormKitControlProps<IFieldProjectFolderValue> {
  config: ColumnsType<ProjectGroupListSchemaFragment | ProjectListSchemaFragment>
  tableName: string
  companyId: string
  excludedIds?: string[]
  type?: 'groups'
  search?: string
}

const FieldProjectFolder: React.FC<IFieldProjectFolderProps> = ({
  tableName,
  config,
  companyId,
  excludedIds,
  type,
  value,
  onChange,
  name,
  search,
}) => {
  const safeValue = isMixed(value) ? undefined : value
  const { paginationConfig, pagination, sortConfig, order } = useTableState(tableName, {
    defaultSort: 'name',
  })

  const { data: groupsData, loading } = useProjectGroupAll({
    companyId,
    parentId: search ? 'root' : safeValue?.categoryId || 'root',
    search: { pagination, order, type, excludedIds, query: search },
  })
  const projectGroup = groupsData?.breadcrumb || null
  const { groups = [], total = 0, projects = [] } = groupsData?.data || {}

  const onClickBreadcrumbsTitle = useCallback(
    (breadcrumbsItem: BreadcrumbItem) => {
      const { color, id = 'root' } =
        projectGroup?.breadcrumbs?.find((item) => item.id === breadcrumbsItem.id) || {}
      onChange({ name, value: { categoryId: id, color } })
    },
    [name, onChange, projectGroup?.breadcrumbs],
  )

  const handleSetCategoryId = useCallback(
    (
      _e: React.MouseEvent<Element, MouseEvent>,
      item: Highlighted<ProjectGroupListSchemaFragment | ProjectListSchemaFragment>,
    ) => {
      paginationConfig.setPage(1)
      const categoryId = checkIsProject(item) ? item.projectGroupId || '' : item.id
      const { nodeName, ...restItem } = item
      return onChange({
        name,
        value: {
          categoryId,
          item: restItem,
          ...(checkIsFolder(item) && { color: item.color }),
        },
      })
    },
    [name, onChange, paginationConfig],
  )

  const dataRaw = useMemo(
    () => (type === 'groups' ? groups : [...groups, ...projects]),
    [type, groups, projects],
  )
  const data = useHighlightedItems(dataRaw, search)

  const breadcrumbsItems: BreadcrumbItem[] = useMemo(() => {
    return [
      { name: t('page.catalog.folders'), id: 'root' },
      ...(projectGroup?.breadcrumbs?.map(({ id, name }) => ({ id, name: name || '' })) || []),
    ]
  }, [projectGroup])

  return (
    <div className={s.root}>
      <Breadcrumbs items={breadcrumbsItems} onClickBreadcrumbs={onClickBreadcrumbsTitle} />
      <div className={s.table}>
        <ModalTable
          columns={config}
          dataSource={data}
          defaultSort='name'
          empty={getTableErrorInfo({
            search: { isError: Boolean(search) },
            empty: {
              isError: !data.length,
              text: (
                <div className={s.emptyContainer}>
                  <span>{t('modal.projectFolder.emptyTable')}</span>
                  <span>{t('modal.projectFolder.addYourFirst')}</span>
                </div>
              ),
            },
          })}
          loading={loading}
          name={tableName}
          onClickRow={handleSetCategoryId}
          paginationConfig={paginationConfig}
          sortConfig={sortConfig}
          total={total}
          pagination
        />
      </div>
    </div>
  )
}

export default React.memo(FieldProjectFolder) as typeof FieldProjectFolder
